import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FileSpreadsheet,
  CalendarDays,
  Briefcase,
  LifeBuoy,
  FolderArchive,
  ListChecks,
  Target,
  CheckCircle,
  TrendingUp,
  Users,
  Building2,
  Clock,
  ArrowUpRight,
  ArrowDownLeft,
} from "lucide-react";
import ExcelJS from "exceljs";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import StatCard from "../components/ui/StatCard";

// ─── API Helper (بنفس أسلوب PlanAdmin.jsx) ──────────────────────────────────
const BASE_URL = process.env.REACT_APP_API_URL || "/api";

async function apiRequest(path) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}${path}`, {
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(data.message || `حدث خطأ غير متوقع (status: ${res.status})`);
  return data;
}

// ─── تصدير جدول عام إلى Excel ────────────────────────────────────────────────
async function exportRowsToExcel({ sheetName, fileName, columns, rows }) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sheetName, { views: [{ rightToLeft: true }] });
  ws.columns = columns.map((c) => ({ header: c.header, key: c.key, width: c.width || 20 }));
  ws.getRow(1).font = { bold: true };
  ws.getRow(1).alignment = { horizontal: "center", vertical: "middle" };
  rows.forEach((r) => ws.addRow(r));
  ws.eachRow((row) => {
    row.eachCell((cell) => {
      cell.alignment = { horizontal: "right", vertical: "middle", wrapText: true };
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── مكوّن صف تجميعي (فئة + عدد + شريط نسبة) ────────────────────────────────
function BreakdownRow({ label, count, total, color = "#546E7A" }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-4 py-2.5">
      <span className="w-40 shrink-0 text-sm font-medium text-[#263238] text-right truncate">
        {label}
      </span>
      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="w-14 shrink-0 text-sm font-bold text-gray-500">{count}</span>
    </div>
  );
}

function groupCount(arr, keyFn) {
  const map = new Map();
  arr.forEach((item) => {
    const key = keyFn(item) || "غير محدد";
    map.set(key, (map.get(key) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

// ══════════════════════════════════════════════════════════════════════════
// 1. تقرير الخطة
// ══════════════════════════════════════════════════════════════════════════
function PlanReport() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await apiRequest("/plan/periods");
        setPeriods(data);
        if (data.length > 0) setPeriodId(data[0].id);
        else setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!periodId) return;
    (async () => {
      try {
        setLoading(true);
        const flat = await apiRequest(`/plan/periods/${periodId}/activities`);
        setActivities(flat.filter((a) => !a.parentActivityId));
        setError("");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [periodId]);

  const totals = useMemo(() => {
    const planned = activities.reduce((s, a) => s + Number(a.plannedVolume || 0), 0);
    const actual = activities.reduce((s, a) => s + Number(a.actualVolume || 0), 0);
    const pct = planned > 0 ? Math.round((actual / planned) * 100) : 0;
    return { planned, actual, pct, count: activities.length };
  }, [activities]);

  const currentPeriod = periods.find((p) => p.id === periodId);

  const handleExport = () => {
    exportRowsToExcel({
      sheetName: "تقرير الخطة",
      fileName: `تقرير_الخطة_${currentPeriod?.periodLabel || ""}.xlsx`,
      columns: [
        { header: "النشاط", key: "name", width: 45 },
        { header: "الوحدة", key: "unit", width: 15 },
        { header: "المخطط", key: "planned", width: 12 },
        { header: "المنجز", key: "actual", width: 12 },
        { header: "نسبة الإنجاز %", key: "pct", width: 14 },
      ],
      rows: activities.map((a) => ({
        name: a.name,
        unit: a.unit || "",
        planned: a.plannedVolume,
        actual: a.actualVolume,
        pct:
          a.plannedVolume > 0
            ? Math.round((a.actualVolume / a.plannedVolume) * 100)
            : 0,
      })),
    });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <select
          dir="rtl"
          value={periodId || ""}
          onChange={(e) => setPeriodId(Number(e.target.value))}
          className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white outline-none appearance-none"
        >
          {periods.map((p) => (
            <option key={p.id} value={p.id}>
              {p.periodLabel || `فترة #${p.id}`}
            </option>
          ))}
        </select>
        <button
          onClick={handleExport}
          disabled={activities.length === 0}
          className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold transition shadow-md disabled:opacity-50"
        >
          <FileSpreadsheet size={16} /> تصدير Excel
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm text-right">
          {error}
        </div>
      )}

      {periods.length === 0 && !loading ? (
        <div className="py-16 text-center text-gray-400 text-sm">لا توجد فترات خطة بعد</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="عدد الأنشطة"
              value={totals.count}
              icon={<ListChecks size={36} className="text-[#546E7A]" />}
              badge="نشط"
              badgeColor="bg-blue-50 text-blue-600"
            />
            <StatCard
              title="إجمالي المخطط"
              value={totals.planned}
              icon={<Target size={36} className="text-[#546E7A]" />}
              badge="مخطط"
              badgeColor="bg-purple-50 text-purple-600"
            />
            <StatCard
              title="إجمالي المنجز"
              value={totals.actual}
              icon={<CheckCircle size={36} className="text-[#546E7A]" />}
              badge="منجز"
              badgeColor="bg-green-50 text-green-600"
            />
            <StatCard
              title="نسبة الإنجاز"
              value={`${totals.pct}%`}
              icon={<TrendingUp size={36} className="text-[#546E7A]" />}
              badge={totals.pct >= 70 ? "جيد" : "منخفض"}
              badgeColor={
                totals.pct >= 70 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
              }
            />
          </div>

          <div className="bg-gray-50 rounded-2xl p-5">
            {loading ? (
              <div className="py-10 text-center text-gray-400 text-sm">جاري التحميل...</div>
            ) : activities.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">لا توجد أنشطة في هذه الفترة</div>
            ) : (
              activities.map((a) => (
                <BreakdownRow
                  key={a.id}
                  label={a.name}
                  count={a.actualVolume}
                  total={a.plannedVolume || 1}
                  color={
                    a.plannedVolume > 0 && a.actualVolume / a.plannedVolume >= 0.7
                      ? "#2E7D32"
                      : "#546E7A"
                  }
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// 2. تقرير الموظفين
// ══════════════════════════════════════════════════════════════════════════
function EmployeesReport() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await apiRequest("/employees");
        setEmployees(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byDepartment = useMemo(
    () => groupCount(employees, (e) => e.departmentName),
    [employees],
  );
  const byGrade = useMemo(
    () => groupCount(employees, (e) => e.financialGrade),
    [employees],
  );

  const handleExport = () => {
    exportRowsToExcel({
      sheetName: "تقرير الموظفين",
      fileName: "تقرير_الموظفين.xlsx",
      columns: [
        { header: "الاسم", key: "name", width: 30 },
        { header: "رقم الملف", key: "fileNumber", width: 15 },
        { header: "الإدارة", key: "departmentName", width: 25 },
        { header: "الدرجة المالية", key: "financialGrade", width: 15 },
        { header: "المجموعة النوعية", key: "groupType", width: 15 },
        { header: "الوظيفة", key: "jobTitle", width: 20 },
      ],
      rows: employees,
    });
  };

  return (
    <div>
      <div className="flex justify-end mb-5">
        <button
          onClick={handleExport}
          disabled={employees.length === 0}
          className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold transition shadow-md disabled:opacity-50"
        >
          <FileSpreadsheet size={16} /> تصدير Excel
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm text-right">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm">جاري التحميل...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="إجمالي الموظفين"
              value={employees.length}
              icon={<Users size={36} className="text-[#546E7A]" />}
              badge="الكل"
              badgeColor="bg-blue-50 text-blue-600"
            />
            <StatCard
              title="عدد الإدارات"
              value={byDepartment.length}
              icon={<Building2 size={36} className="text-[#546E7A]" />}
              badge="إدارات"
              badgeColor="bg-purple-50 text-purple-600"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-2xl p-5">
              <h4 className="text-sm font-bold text-[#263238] mb-3 text-right">التوزيع حسب الإدارة</h4>
              {byDepartment.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">لا توجد بيانات</p>
              ) : (
                byDepartment.map((d) => (
                  <BreakdownRow key={d.label} label={d.label} count={d.count} total={employees.length} />
                ))
              )}
            </div>
            <div className="bg-gray-50 rounded-2xl p-5">
              <h4 className="text-sm font-bold text-[#263238] mb-3 text-right">التوزيع حسب الدرجة المالية</h4>
              {byGrade.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">لا توجد بيانات</p>
              ) : (
                byGrade.map((d) => (
                  <BreakdownRow key={d.label} label={d.label} count={d.count} total={employees.length} color="#8E24AA" />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// 3. تقرير المشاكل
// ══════════════════════════════════════════════════════════════════════════
function IssuesReport() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await apiRequest("/issues");
        setIssues(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const solved = issues.filter((i) => i.solution && i.solution.trim()).length;
  const pending = issues.length - solved;
  const byCategory = useMemo(() => groupCount(issues, (i) => i.categoryName), [issues]);
  const byChannel = useMemo(() => groupCount(issues, (i) => i.channelName), [issues]);

  const handleExport = () => {
    exportRowsToExcel({
      sheetName: "تقرير المشاكل",
      fileName: "تقرير_المشاكل.xlsx",
      columns: [
        { header: "المشكلة", key: "problem", width: 45 },
        { header: "الفئة", key: "categoryName", width: 20 },
        { header: "القناة", key: "channelName", width: 20 },
        { header: "آلية الحل", key: "mechanismName", width: 20 },
        { header: "الحالة", key: "status", width: 12 },
      ],
      rows: issues.map((i) => ({
        problem: i.problem,
        categoryName: i.categoryName || "",
        channelName: i.channelName || "",
        mechanismName: i.mechanismName || "",
        status: i.solution && i.solution.trim() ? "منتهية" : "قيد المعالجة",
      })),
    });
  };

  return (
    <div>
      <div className="flex justify-end mb-5">
        <button
          onClick={handleExport}
          disabled={issues.length === 0}
          className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold transition shadow-md disabled:opacity-50"
        >
          <FileSpreadsheet size={16} /> تصدير Excel
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm text-right">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm">جاري التحميل...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="إجمالي المشاكل"
              value={issues.length}
              icon={<LifeBuoy size={36} className="text-[#546E7A]" />}
              badge="الكل"
              badgeColor="bg-blue-50 text-blue-600"
            />
            <StatCard
              title="تم حلها"
              value={solved}
              icon={<CheckCircle size={36} className="text-[#546E7A]" />}
              badge="منتهية"
              badgeColor="bg-green-50 text-green-600"
            />
            <StatCard
              title="قيد المعالجة"
              value={pending}
              icon={<Clock size={36} className="text-[#546E7A]" />}
              badge="معلقة"
              badgeColor="bg-amber-50 text-amber-600"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-2xl p-5">
              <h4 className="text-sm font-bold text-[#263238] mb-3 text-right">التوزيع حسب فئة المشكلة</h4>
              {byCategory.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">لا توجد بيانات</p>
              ) : (
                byCategory.map((d) => (
                  <BreakdownRow key={d.label} label={d.label} count={d.count} total={issues.length} />
                ))
              )}
            </div>
            <div className="bg-gray-50 rounded-2xl p-5">
              <h4 className="text-sm font-bold text-[#263238] mb-3 text-right">التوزيع حسب قناة الإبلاغ</h4>
              {byChannel.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">لا توجد بيانات</p>
              ) : (
                byChannel.map((d) => (
                  <BreakdownRow key={d.label} label={d.label} count={d.count} total={issues.length} color="#0288D1" />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// 4. تقرير المستندات
// ══════════════════════════════════════════════════════════════════════════
function DocumentsReport() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await apiRequest("/documents");
        setDocuments(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const outgoing = documents.filter((d) => d.movementType === "صادر").length;
  const incoming = documents.filter((d) => d.movementType === "وارد").length;
  const byDepartment = useMemo(
    () => groupCount(documents, (d) => d.departmentName),
    [documents],
  );

  const handleExport = () => {
    exportRowsToExcel({
      sheetName: "تقرير المستندات",
      fileName: "تقرير_المستندات.xlsx",
      columns: [
        { header: "الوصف", key: "description", width: 40 },
        { header: "الإدارة", key: "departmentName", width: 22 },
        { header: "الجهة", key: "partyName", width: 22 },
        { header: "نوع الحركة", key: "movementType", width: 12 },
        { header: "تاريخ المستند", key: "documentDate", width: 15 },
      ],
      rows: documents.map((d) => ({
        description: d.description || "",
        departmentName: d.departmentName || "",
        partyName: d.partyName || "",
        movementType: d.movementType || "",
        documentDate: d.documentDate ? String(d.documentDate).split("T")[0] : "",
      })),
    });
  };

  return (
    <div>
      <div className="flex justify-end mb-5">
        <button
          onClick={handleExport}
          disabled={documents.length === 0}
          className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold transition shadow-md disabled:opacity-50"
        >
          <FileSpreadsheet size={16} /> تصدير Excel
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm text-right">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm">جاري التحميل...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="إجمالي المستندات"
              value={documents.length}
              icon={<FolderArchive size={36} className="text-[#546E7A]" />}
              badge="الكل"
              badgeColor="bg-blue-50 text-blue-600"
            />
            <StatCard
              title="صادر"
              value={outgoing}
              icon={<ArrowUpRight size={36} className="text-[#546E7A]" />}
              badge="صادر"
              badgeColor="bg-green-50 text-green-600"
            />
            <StatCard
              title="وارد"
              value={incoming}
              icon={<ArrowDownLeft size={36} className="text-[#546E7A]" />}
              badge="وارد"
              badgeColor="bg-blue-50 text-blue-600"
            />
          </div>

          <div className="bg-gray-50 rounded-2xl p-5">
            <h4 className="text-sm font-bold text-[#263238] mb-3 text-right">التوزيع حسب الإدارة</h4>
            {byDepartment.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">لا توجد بيانات</p>
            ) : (
              byDepartment.map((d) => (
                <BreakdownRow key={d.label} label={d.label} count={d.count} total={documents.length} />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── تعريف التبويبات ─────────────────────────────────────────────────────────
const TABS = [
  { key: "plan", label: "تقرير الخطة", icon: CalendarDays, Component: PlanReport },
  { key: "employees", label: "تقرير الموظفين", icon: Briefcase, Component: EmployeesReport },
  { key: "issues", label: "تقرير المشاكل", icon: LifeBuoy, Component: IssuesReport },
  { key: "documents", label: "تقرير المستندات", icon: FolderArchive, Component: DocumentsReport },
];

// ─── ROOT COMPONENT ───────────────────────────────────────────────────────────
function Reports() {
  useEffect(() => {
    document.title = "تقارير - الإدارة العامة للعمليات والتوقيع الإلكتروني";
  }, []);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("plan");

  const ActiveComponent = TABS.find((t) => t.key === activeTab)?.Component;

  return (
    <div className="flex min-h-screen bg-[#F8F9FA] font-ibm" dir="rtl">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div
        className={`flex-1 transition-all duration-300 p-4 md:p-8 ${isSidebarOpen ? "lg:mr-64" : "lg:mr-20"}`}
      >
        <Header isSidebarOpen={isSidebarOpen} setSidebarOpen={setIsSidebarOpen} title="تقارير الإدارة" />

        {/* Hero Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6 text-right">
          <div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#546E7A]">
              تقارير الإدارة
            </h2>
            <p className="text-gray-400 mt-1">
              تقارير الخطة والموظفين والمشاكل والمستندات، مع إمكانية التصدير إلى Excel
            </p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Tabs */}
          <div className="flex flex-wrap gap-0 px-6 pt-5 border-b border-gray-100 overflow-x-auto">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`px-5 py-3 text-sm font-medium transition-all border-b-2 flex items-center gap-2 whitespace-nowrap ${
                    activeTab === t.key
                      ? "text-[#546E7A] border-[#546E7A]"
                      : "text-gray-400 border-transparent hover:text-gray-600"
                  }`}
                >
                  <Icon size={16} /> {t.label}
                </button>
              );
            })}
          </div>

          <div className="p-4 md:p-6">{ActiveComponent && <ActiveComponent />}</div>
        </div>
      </div>
    </div>
  );
}

export default Reports;